import { useEffect } from 'react';
import { toast } from 'react-toastify';

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiServiceProxy } from 'v2/services/api-proxy';

interface IDownloadFile {
  id: number;
  name: string;
  extension: string;
  url_download: string;
}

export const useDownload = (hash: string) => {
  const listDownloads = useQuery({
    queryKey: ['downloadFile', hash],
    queryFn: async () => {
      const result = await apiServiceProxy.get<IDownloadFile[]>(`/downloadfile/${hash}`);
      return result.data;
    },
    enabled: !!hash
  });

  useEffect(() => {
    if (listDownloads.isError) {
      toast.error('Ops, erro na listagem de downloads');
    }
  }, [listDownloads.isError]);

  const getDownloadUrl = async (file: IDownloadFile): Promise<string | null> => {
    const isValid = await validateDownloadUrl(file.url_download);

    if (isValid) {
      return file.url_download;
    }

    const refreshedDownloads = await listDownloads.refetch();

    if (refreshedDownloads.isError) {
      return getDownloadUrl(file);
    }

    const refreshedFile = refreshedDownloads.data?.find(item => item.id === file.id);

    if (!refreshedFile) {
      return getDownloadUrl(file);
    }

    return getDownloadUrl(refreshedFile);
  };

  const validateDownloadUrl = async (url: string): Promise<boolean> => {
    const hash = url.split('/').pop();
    const result = await apiServiceProxy.post('/validatedownloadhash', { hash });
    return result.data?.status;
  };

  const downloadFile = useMutation({
    mutationFn: async (file: IDownloadFile) => {
      const url = await getDownloadUrl(file);

      if (!url) {
        toast.error('Link de download inválido');
        return;
      }

      const link = document.createElement('a');
      link.setAttribute('download', 'true');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  });

  return {
    downloadFile: (file: IDownloadFile) => downloadFile.mutateAsync(file),
    isDownloading: listDownloads.isFetching,
    downloads: listDownloads.data
  };
};
